import { useEffect, useState } from "react";
import { BRAND } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StatsPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API}/analytics/summary`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setError(true));
  }, []);

  const card = "border border-amber-500/15 bg-[#141210] p-6";

  return (
    <div className="min-h-screen bg-ink px-6 py-16 text-[#FFF5EB] sm:px-10 lg:px-16" data-testid="stats-page">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center gap-3">
          <img src={BRAND.emblem} alt="" className="h-8 w-auto" />
          <h1 className="font-serif text-2xl uppercase tracking-tight text-amber-100">Site Analytics</h1>
        </div>
        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          Private — unlisted page, aggregate counts only, no personal data
        </p>

        {error && <p className="mt-10 text-sm text-neutral-400">Could not load stats.</p>}
        {!data && !error && <p className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">Loading…</p>}

        {data && (
          <>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className={card} data-testid="stats-total-visits">
                <p className="font-serif text-4xl text-amber-400">{data.pageviews}</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Page Views</p>
              </div>
              <div className={card} data-testid="stats-reached-contact">
                <p className="font-serif text-4xl text-amber-400">{data.reached_contact}</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Reached Contact</p>
              </div>
              <div className={card} data-testid="stats-gallery-opens">
                <p className="font-serif text-4xl text-amber-400">{data.gallery_opens}</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Photo Opens</p>
              </div>
              <div className={card} data-testid="stats-inquiries">
                <p className="font-serif text-4xl text-amber-400">{data.inquiries}</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Inquiries Sent</p>
              </div>
            </div>

            <div className="mt-10 grid gap-4 lg:grid-cols-2">
              <div className={card}>
                <h2 className="font-serif text-lg uppercase text-amber-100">Most Opened Photos</h2>
                {data.top_photos.length === 0 && <p className="mt-4 text-sm text-neutral-500">No photo opens yet.</p>}
                <ul className="mt-4 space-y-2">
                  {data.top_photos.map((p) => (
                    <li key={p.photo} className="flex items-center justify-between border-b border-neutral-800/60 pb-2 text-sm">
                      <span className="text-neutral-300">{p.photo}</span>
                      <span className="font-mono text-ember">{p.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={card}>
                <h2 className="font-serif text-lg uppercase text-amber-100">Visitors Came From</h2>
                {data.top_referrers.length === 0 && <p className="mt-4 text-sm text-neutral-500">No outside referrers yet — most visits are direct.</p>}
                <ul className="mt-4 space-y-2">
                  {data.top_referrers.map((r) => (
                    <li key={r.source} className="flex items-center justify-between border-b border-neutral-800/60 pb-2 text-sm">
                      <span className="max-w-[70%] truncate text-neutral-300">{r.source}</span>
                      <span className="font-mono text-ember">{r.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className={`${card} mt-4`}>
              <h2 className="font-serif text-lg uppercase text-amber-100">Last 30 Days — Page Views</h2>
              {data.daily.length === 0 && <p className="mt-4 text-sm text-neutral-500">No visits recorded yet.</p>}
              <div className="mt-6 flex h-32 items-end gap-1">
                {data.daily.map((d) => (
                  <div key={d.date} className="group relative flex-1">
                    <div
                      className="w-full bg-gradient-to-t from-flame to-ember transition-all duration-300 group-hover:to-gold"
                      style={{ height: `${Math.max(4, (d.count / data.max_daily) * 120)}px` }}
                      title={`${d.date}: ${d.count} views`}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-2 flex justify-between font-mono text-[9px] uppercase tracking-[0.15em] text-neutral-600">
                <span>{data.daily[0]?.date}</span>
                <span>{data.daily[data.daily.length - 1]?.date}</span>
              </div>
            </div>
          </>
        )}

        <a href="/" className="mt-10 inline-block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 hover:text-amber-400">
          ← Back to site
        </a>
      </div>
    </div>
  );
}
